import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../api/apiService';
import GitlabMarkdownRenderer from './GitlabMarkdownRenderer';
import { useNotification } from '../context/NotificationContext';
import { useTaskPolling } from '../context/TaskPollingContext';

const ExtractAttributes = () => {
  const { addToast } = useNotification();
  const { registerTask } = useTaskPolling();
  // Auto-resize textarea function
  const autoResizeTextarea = (textareaRef) => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState(''); // 'codebase' or 'document'
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddAttributeForm, setShowAddAttributeForm] = useState(false);
  const [showEditAttributeForm, setShowEditAttributeForm] = useState(null);
  const [newAttribute, setNewAttribute] = useState({
    name: '',
    description: '',
    data_type: '',
    validation_rules: null,
    validation_logic: '',
    error_message: '',
    required: null,
    default_value: ''
  });
  const [editAttribute, setEditAttribute] = useState({
    name: '',
    description: '',
    data_type: '',
    validation_rules: null,
    validation_logic: '',
    error_message: '',
    required: null,
    default_value: ''
  });
  const editAttributeDescRef = useRef(null);
  const newAttributeDescRef = useRef(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, type: null, id: null, name: '' });
  const [attributesExtracted, setAttributesExtracted] = useState(false);
  const [extractionAttempted, setExtractionAttempted] = useState(false);
  const [hasDetailedAttributes, setHasDetailedAttributes] = useState(false);
  const [extractionStarted, setExtractionStarted] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  // Watch for task completion to reload attributes
  useEffect(() => {
    if (!activeTaskId) return;

    const checkCompletion = setInterval(async () => {
      try {
        const task = await apiService.getTaskStatus(activeTaskId);
        
        if (task.status === 'completed') {
          clearInterval(checkCompletion);
          setActiveTaskId(null);
          setExtractionStarted(false);
          // Reload detailed attributes from DB
          try {
            const result = await apiService.getStoredAttributesWithDetails(selectedProject);
            setAttributes(result || []);
            setHasDetailedAttributes(true);
            setAttributesExtracted(true);
          } catch (e) {
            console.error('Failed to reload attributes after completion:', e);
          }
        } else if (task.status === 'failed') {
          clearInterval(checkCompletion);
          setActiveTaskId(null);
          setExtractionStarted(false);
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, 3000);

    return () => {
      clearInterval(checkCompletion);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaskId, selectedProject]);

  // Auto-resize textareas when they appear
  useEffect(() => {
    if (showEditAttributeForm) {
      setTimeout(() => autoResizeTextarea(editAttributeDescRef), 0);
    }
  }, [showEditAttributeForm]);

  useEffect(() => {
    if (showAddAttributeForm) {
      setTimeout(() => autoResizeTextarea(newAttributeDescRef), 0);
    }
  }, [showAddAttributeForm]);

  const fetchProjects = async () => {
    try {
      const projectList = await apiService.getProjects();
      setProjects(projectList);
    } catch (err) {
      setError('Failed to fetch projects: ' + err.message);
    }
  };


  const handleProjectChange = async (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);

    // Find the project in projects
    const selectedProjectObj = projects.find(p => p.id === projectId);
    if (selectedProjectObj) {
      setProjectName(selectedProjectObj.name);
      setProjectType(selectedProjectObj.type);
    } else {
      // If project is not found, clear the project name and type
      setProjectName('');
      setProjectType('');
    }

    // Reset attributes extracted state when project changes
    setAttributesExtracted(false);
    setExtractionAttempted(false); // Reset extraction attempted state when project changes
    setHasDetailedAttributes(false); // Reset detailed attributes status when project changes

    if (projectId) {
      try {
        setLoading(true);
        let attributesData = [];

        if (selectedProjectObj.type === 'document') {
          // For documents, we need to implement document-specific attribute fetching
          // For now, we'll use the same API but may need to update this later
          // Using the same endpoint since document attributes might be stored similarly
          attributesData = await apiService.getAttributesByProject(projectId);
        } else {
          // Fetch attributes for codebase project
          attributesData = await apiService.getAttributesByProject(projectId);
        }

        setAttributes(attributesData || []);

        // Check if detailed attributes exist for this project
        // This check now works for both codebase and document projects
        try {
          const detailedCheck = await apiService.getHasDetailedExtraction(projectId);
          setHasDetailedAttributes(detailedCheck.has_detailed_attributes);
        } catch (err) {
          console.error('Error checking detailed extraction:', err);
          // Set to false if the call fails
          setHasDetailedAttributes(false);
        }
      } catch (err) {
        setError('Failed to fetch attributes: ' + err.message);
        setAttributes([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExtractAttributes = async () => {
    if (!selectedProject || !projectName) return;

    try {
      setLoading(true);

      // Fetch existing attributes from the database for the selected project
      let attributesData = [];
      attributesData = await apiService.getAttributesByProject(selectedProject);

      setAttributes(attributesData || []);
      setAttributesExtracted(attributesData && attributesData.length > 0); // Mark that attributes have been extracted if there are attributes
      setExtractionAttempted(true); // Mark that extraction has been attempted

      // Check if detailed attributes exist for this project
      // This check now works for both codebase and document projects
      try {
        const detailedCheck = await apiService.getHasDetailedExtraction(selectedProject);
        setHasDetailedAttributes(detailedCheck.has_detailed_attributes);
      } catch (err) {
        console.error('Error checking detailed extraction:', err);
        // Set to false if the call fails
        setHasDetailedAttributes(false);
      }
      setError(null);
    } catch (err) {
      setError('Failed to fetch attributes: ' + err.message);
      setExtractionAttempted(true); // Still mark extraction as attempted even if it failed
    } finally {
      setLoading(false);
    }
  };

  const handleExtractDetails = async () => {
    if (!selectedProject || !projectName) return;

    try {
      setLoading(true);
      setError(null);

      // Call /api/has_detailed_extraction/{project_id} to check if detailed attributes exist
      const detailedCheck = await apiService.getHasDetailedExtraction(selectedProject);

      if (detailedCheck.has_detailed_attributes) {
        // If has_detailed_attributes is true, call /api/stored_attributes_with_details/{project_id}
        const result = await apiService.getStoredAttributesWithDetails(selectedProject);
        setAttributes(result || []);
        setHasDetailedAttributes(true);
      } else {
        // If has_detailed_attributes is false, call background extraction
        const response = await apiService.extractAttributes(selectedProject, projectName);

        // Store task_id and start polling
        const tid = response.task_id;
        setActiveTaskId(tid);
        setExtractionStarted(true);

        // Create toast notification
        const newToastId = addToast({
          title: projectName,
          message: 'Starting attribute extraction...',
          type: 'info',
          status: 'running',
        });

        // Register with global polling service
        registerTask(tid, newToastId, projectName, 'attribute_detail_extraction');

        setLoading(false);
        return; // polling takes over from here
      }

      // Update the status to reflect the current state
      setHasDetailedAttributes(true);
    } catch (err) {
      console.error('Extraction error:', err);
      setError('Extraction failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAttribute = async () => {
    if (!newAttribute.name || !selectedProject) return;

    try {
      const attributeData = {
        project_id: selectedProject,
        project_name: projectName,
        attribute_name: newAttribute.name,
        description: newAttribute.description || '',
        data_type: newAttribute.data_type || '',
        required: newAttribute.required,
        default_value: newAttribute.default_value || '',
        validation_rules: newAttribute.validation_rules || null,
        validation_logic: newAttribute.validation_logic || '',
        error_message: newAttribute.error_message || ''
      };

      let newAttributeResult;
      if (projectType === 'document') {
        // For documents, we might need a different API call
        // For now, we'll use the same API but may need to update this later
        newAttributeResult = await apiService.addAttribute(attributeData);
      } else {
        // For codebase projects
        newAttributeResult = await apiService.addAttribute(attributeData);
      }

      // Add to local state
      setAttributes([...attributes, newAttributeResult]);
      setNewAttribute({ name: '' });
      setShowAddAttributeForm(false);

      // Update the extracted state to show the attributes list
      setAttributesExtracted(true);
      setExtractionAttempted(true);
    } catch (err) {
      setError('Failed to add attribute: ' + err.message);
    }
  };

  const handleUpdateAttribute = async (attributeId) => {
    if (!editAttribute.name) return;

    try {
      const attributeData = {
        attribute_name: editAttribute.name,
        description: editAttribute.description,
        data_type: editAttribute.data_type || '',
        required: editAttribute.required,
        default_value: editAttribute.default_value || '',
        validation_rules: editAttribute.validation_rules || null,
        validation_logic: editAttribute.validation_logic || '',
        error_message: editAttribute.error_message || ''
      };

      const updatedAttribute = await apiService.updateAttribute(attributeId, attributeData);

      // Update in local state
      setAttributes(attributes.map(a =>
        a.id === attributeId ? { ...a, ...updatedAttribute } : a
      ));
      setShowEditAttributeForm(null);
      setEditAttribute({ name: '' });
    } catch (err) {
      setError('Failed to update attribute: ' + err.message);
    }
  };

  const handleDeleteAttribute = async (attributeId, attributeName) => {
    setDeleteConfirmation({
      show: true,
      type: 'attribute',
      id: attributeId,
      name: attributeName || 'this attribute'
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.id) return;

    try {
      if (deleteConfirmation.type === 'attribute') {
        await apiService.deleteAttribute(deleteConfirmation.id);
        setAttributes(attributes.filter(a => a.id !== deleteConfirmation.id));
      }
    } catch (err) {
      setError('Failed to delete ' + deleteConfirmation.type + ': ' + err.message);
    } finally {
      setDeleteConfirmation({ show: false, type: null, id: null, name: '' });
    }
  };

  const handleDownloadAttributes = () => {
    const dataStr = JSON.stringify(attributes, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const fileName = projectName ? projectName.replace(' ', '_') : 'project';
    const exportFileDefaultName = `${fileName}_attributes.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Extract Attributes</h2>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-blue-700">Extract field attributes with validation rules and messages from your processed codebase or document projects.</p>
      </div>

      <div className="mb-6">
        <label htmlFor="attributeProjectSelect" className="block text-sm font-medium text-gray-700 mb-2">
          Select Project
        </label>
        <select
          id="attributeProjectSelect"
          value={selectedProject}
          onChange={handleProjectChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select a project</option>
          {projects.map(item => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-6">
        <button
          onClick={handleExtractAttributes}
          disabled={!selectedProject || loading || attributesExtracted}
          className={`py-2 px-4 rounded-md text-white font-medium ${!selectedProject || loading || attributesExtracted
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
            }`}
        >
          {loading ? 'Extracting...' : attributesExtracted ? 'Attributes Extracted' : 'Extract Attributes'}
        </button>

        {/* Inline status display while processing */}
        {extractionStarted && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <p className="text-sm font-medium text-blue-800">
                Extraction running in background...
              </p>
            </div>
            <p className="text-xs text-blue-600">
              Check the top-right corner for live status updates. Claude is analyzing your codebase.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {attributesExtracted && attributes.length > 0 ? (
        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-4">Extracted Attributes ({attributes.length} total)</h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                    Attribute
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                    Data Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">
                    Required
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                    Default Value
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                    Validation Rules
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                    Validation Logic
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                    Validation Error Messages
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attributes.map((attribute, i) => (
                  <tr key={attribute.id || i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs break-words">
                      {showEditAttributeForm === attribute.id ? (
                        <input
                          type="text"
                          value={editAttribute.name}
                          onChange={(e) => setEditAttribute({ ...editAttribute, name: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        attribute.attribute_name || 'Untitled Attribute'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs break-words">
                      {showEditAttributeForm === attribute.id ? (
                        <input
                          type="text"
                          value={editAttribute.data_type}
                          onChange={(e) => setEditAttribute({ ...editAttribute, data_type: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        attribute.data_type || 'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs break-words">
                      {showEditAttributeForm === attribute.id ? (
                        <select
                          value={editAttribute.required !== null ? editAttribute.required.toString() : ''}
                          onChange={(e) => setEditAttribute({ ...editAttribute, required: e.target.value === '' ? null : e.target.value === 'true' })}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Required Status</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        attribute.required !== null ? (attribute.required ? 'Yes' : 'No') : 'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs break-words">
                      {showEditAttributeForm === attribute.id ? (
                        <input
                          type="text"
                          value={editAttribute.default_value}
                          onChange={(e) => setEditAttribute({ ...editAttribute, default_value: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        attribute.default_value || 'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs break-words">
                      {showEditAttributeForm === attribute.id ? (
                        <input
                          type="text"
                          value={typeof editAttribute.validation_rules === 'string'
                            ? (() => {
                              try {
                                // Attempt to parse the JSON string
                                const parsed = JSON.parse(editAttribute.validation_rules);
                                return typeof parsed === 'string' ? parsed : editAttribute.validation_rules;
                              } catch (e) {
                                // If parsing fails, return the original string
                                return editAttribute.validation_rules;
                              }
                            })()
                            : typeof editAttribute.validation_rules === 'object'
                              ? Array.isArray(editAttribute.validation_rules)
                                ? editAttribute.validation_rules.join(', ')
                                : JSON.stringify(editAttribute.validation_rules)
                              : editAttribute.validation_rules || ''}
                          onChange={(e) => setEditAttribute({ ...editAttribute, validation_rules: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : attribute.validation_rules ? (
                        <div className="max-w-none">
                          <GitlabMarkdownRenderer content={
                            typeof attribute.validation_rules === 'string'
                              ? (() => {
                                try {
                                  // Attempt to parse the JSON string
                                  const parsed = JSON.parse(attribute.validation_rules);
                                  return typeof parsed === 'string' ? parsed : attribute.validation_rules;
                                } catch (e) {
                                  // If parsing fails, return the original string
                                  return attribute.validation_rules;
                                }
                              })()
                              : typeof attribute.validation_rules === 'object'
                                ? Array.isArray(attribute.validation_rules)
                                  ? attribute.validation_rules.join(', ')
                                  : JSON.stringify(attribute.validation_rules)
                                : attribute.validation_rules
                          } />
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs break-words">
                      {showEditAttributeForm === attribute.id ? (
                        <input
                          type="text"
                          value={editAttribute.validation_logic || ''}
                          onChange={(e) => setEditAttribute({ ...editAttribute, validation_logic: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        attribute.validation_logic || 'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs break-words">
                      {showEditAttributeForm === attribute.id ? (
                        <input
                          type="text"
                          value={editAttribute.error_message}
                          onChange={(e) => setEditAttribute({ ...editAttribute, error_message: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        attribute.error_message || 'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                      <div className="flex space-x-2">
                        {showEditAttributeForm === attribute.id ? (
                          <>
                            <button
                              onClick={() => handleUpdateAttribute(attribute.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setShowEditAttributeForm(null);
                                setEditAttribute({
                                  name: attribute.attribute_name || '',
                                  description: attribute.description || '',
                                  data_type: attribute.data_type || '',
                                  validation_rules: attribute.validation_rules ? (() => {
                                    try {
                                      // Attempt to parse the JSON string
                                      const parsed = JSON.parse(attribute.validation_rules);
                                      return typeof parsed === 'string' ? parsed : attribute.validation_rules;
                                    } catch (e) {
                                      // If parsing fails, return the original string
                                      return attribute.validation_rules;
                                    }
                                  })() : null,
                                  validation_logic: attribute.validation_logic || '',
                                  error_message: attribute.error_message || '',
                                  required: attribute.required || null,
                                  default_value: attribute.default_value || ''
                                });
                              }}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => {
                              setEditAttribute({
                                name: attribute.attribute_name || '',
                                description: attribute.description || '',
                                data_type: attribute.data_type || '',
                                validation_rules: attribute.validation_rules ? (() => {
                                  try {
                                    // Attempt to parse the JSON string
                                    const parsed = JSON.parse(attribute.validation_rules);
                                    return typeof parsed === 'string' ? parsed : attribute.validation_rules;
                                  } catch (e) {
                                    // If parsing fails, return the original string
                                    return attribute.validation_rules;
                                  }
                                })() : null,
                                validation_logic: attribute.validation_logic || '',
                                error_message: attribute.error_message || '',
                                required: attribute.required || null,
                                default_value: attribute.default_value || ''
                              });
                              setShowEditAttributeForm(attribute.id);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteAttribute(attribute.id, attribute.attribute_name || 'Untitled Attribute')}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            {!hasDetailedAttributes ? (
              <button
                onClick={() => setShowAddAttributeForm(true)}
                className="text-blue-600 hover:text-blue-800"
              >
                + Add New Attribute
              </button>
            ) : (
              <div className="text-gray-500 text-sm">
                Detailed attributes have been extracted. Manual attribute addition is disabled.
              </div>
            )}
          </div>


          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={handleExtractDetails}
              disabled={!selectedProject || loading || hasDetailedAttributes}
              className={`py-2 px-4 rounded-md text-white font-medium ${!selectedProject || loading || hasDetailedAttributes
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
                }`}
            >
              {loading ? 'Processing...' : hasDetailedAttributes ? 'Details Extracted' : 'Extract Details'}
            </button>
            <button
              onClick={handleDownloadAttributes}
              className="py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Download Attribute JSON
            </button>
          </div>
        </div>
      ) : (
        // Empty state when no attributes are extracted
        <div className="mb-6">
          {selectedProject && extractionAttempted ? (
            <div className="p-8 bg-white border border-gray-200 rounded-lg text-center">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Attributes Found</h3>
              {!hasDetailedAttributes ? (
                <>
                  <p className="text-gray-500 mb-6">No attributes were extracted from the selected project. You can add attributes manually.</p>
                  <button
                    onClick={() => setShowAddAttributeForm(true)}
                    className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    + Add New Attribute
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-500 mb-6">Detailed attributes have been extracted. Manual attribute addition is disabled.</p>
                </>
              )}
            </div>
          ) : (
            <div className="p-8 bg-white border border-gray-200 rounded-lg text-center">
              <div className="text-gray-500 text-5xl mb-4">📁</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a Project and Extract Attributes</h3>
              <p className="text-gray-500 mb-6">Please select a project from the dropdown above and click the 'Extract Attributes' button to analyze the codebase.</p>
            </div>
          )}
        </div>
      )}

      {/* Add Attribute Modal */}
      {showAddAttributeForm && !hasDetailedAttributes && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Attribute</h3>
                <button
                  onClick={() => setShowAddAttributeForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-4">
                  <input
                    type="text"
                    value={newAttribute.name}
                    onChange={(e) => setNewAttribute({ ...newAttribute, name: e.target.value })}
                    placeholder="Attribute Name"
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    value={newAttribute.data_type}
                    onChange={(e) => setNewAttribute({ ...newAttribute, data_type: e.target.value })}
                    placeholder="Data Type"
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    value={newAttribute.default_value}
                    onChange={(e) => setNewAttribute({ ...newAttribute, default_value: e.target.value })}
                    placeholder="Default Value"
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <select
                    value={newAttribute.required !== null ? newAttribute.required.toString() : ''}
                    onChange={(e) => setNewAttribute({ ...newAttribute, required: e.target.value === '' ? null : e.target.value === 'true' })}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Required Status</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                  <input
                    type="text"
                    value={newAttribute.error_message}
                    onChange={(e) => setNewAttribute({ ...newAttribute, error_message: e.target.value })}
                    placeholder="Error Message"
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    value={typeof newAttribute.validation_rules === 'string'
                      ? (() => {
                        try {
                          // Attempt to parse the JSON string
                          const parsed = JSON.parse(newAttribute.validation_rules);
                          return typeof parsed === 'string' ? parsed : newAttribute.validation_rules;
                        } catch (e) {
                          // If parsing fails, return the original string
                          return newAttribute.validation_rules;
                        }
                      })()
                      : typeof newAttribute.validation_rules === 'object'
                        ? Array.isArray(newAttribute.validation_rules)
                          ? newAttribute.validation_rules.join(', ')
                          : JSON.stringify(newAttribute.validation_rules)
                        : newAttribute.validation_rules || ''}
                    onChange={(e) => setNewAttribute({ ...newAttribute, validation_rules: e.target.value })}
                    placeholder="Validation Rules"
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    value={newAttribute.validation_logic || ''}
                    onChange={(e) => setNewAttribute({ ...newAttribute, validation_logic: e.target.value })}
                    placeholder="Validation Logic"
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <textarea
                    ref={newAttributeDescRef}
                    value={newAttribute.description}
                    onChange={(e) => {
                      setNewAttribute({ ...newAttribute, description: e.target.value });
                      autoResizeTextarea(newAttributeDescRef);
                    }}
                    placeholder="Attribute Description (supports Markdown)"
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden"
                    rows="1"
                    style={{ minHeight: '40px' }}
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleAddAttribute}
                    className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Attribute
                  </button>
                  <button
                    onClick={() => setShowAddAttributeForm(false)}
                    className="py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddAttributeForm && hasDetailedAttributes && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add New Attribute</h3>
                <button
                  onClick={() => setShowAddAttributeForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <div className="p-4">
                <div className="text-red-600 mb-4">
                  Detailed attributes have been extracted. Manual attribute addition is disabled.
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAddAttributeForm(false)}
                    className="py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Confirm Deletion</h3>
                <button
                  onClick={() => setDeleteConfirmation({ show: false, type: null, id: null, name: '' })}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <div className="p-4">
                <p className="text-gray-700">
                  Are you sure you want to delete <strong>{deleteConfirmation.name}</strong>?
                  This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3 mt-4">
                  <button
                    onClick={() => setDeleteConfirmation({ show: false, type: null, id: null, name: '' })}
                    className="py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="py-2 px-4 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractAttributes;